import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { searchCapLocations } from './services/cap-mappings.service';

const EMPTY_FORM = {
  csvFacilityName: '',
  csvHuName: '',
  facilityLocation: null,
  huLocation: null,
};

const formatLocationLabel = (location) => {
  if (!location) return '';
  const code = location.shortCode ? ` (${location.shortCode})` : '';
  return `${location.locationName || 'Unnamed'}${code}`;
};

const MappingFormDialog = ({
  open,
  mode,
  clients,
  initialClientName,
  initialMapping,
  onClose,
  onSubmit,
  saving,
}) => {
  const [clientName, setClientName] = useState(initialClientName || '');
  const [form, setForm] = useState(EMPTY_FORM);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [huOptions, setHuOptions] = useState([]);
  const [facilityQuery, setFacilityQuery] = useState('');
  const [huQuery, setHuQuery] = useState('');
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [huLoading, setHuLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setClientName(initialMapping?.clientName || initialClientName || clients[0] || '');
    setError('');
    if (initialMapping) {
      setForm({
        csvFacilityName: initialMapping.csvFacilityName || '',
        csvHuName: initialMapping.csvHuName || '',
        facilityLocation: initialMapping.facilityLocation || null,
        huLocation: initialMapping.huLocation || null,
      });
      setFacilityOptions(initialMapping.facilityLocation ? [initialMapping.facilityLocation] : []);
      setHuOptions(initialMapping.huLocation ? [initialMapping.huLocation] : []);
    } else {
      setForm(EMPTY_FORM);
      setFacilityOptions([]);
      setHuOptions([]);
    }
  }, [open, initialMapping, initialClientName, clients]);

  useEffect(() => {
    if (!open || facilityQuery.trim().length < 1) return undefined;
    let cancelled = false;
    const handle = setTimeout(() => {
      setFacilityLoading(true);
      searchCapLocations(facilityQuery.trim())
        .then((rows) => {
          if (!cancelled) setFacilityOptions(Array.isArray(rows) ? rows : []);
        })
        .catch(() => {
          if (!cancelled) setFacilityOptions([]);
        })
        .finally(() => {
          if (!cancelled) setFacilityLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [facilityQuery, open]);

  useEffect(() => {
    if (!open || huQuery.trim().length < 1) return undefined;
    let cancelled = false;
    const handle = setTimeout(() => {
      setHuLoading(true);
      searchCapLocations(huQuery.trim())
        .then((rows) => {
          if (!cancelled) setHuOptions(Array.isArray(rows) ? rows : []);
        })
        .catch(() => {
          if (!cancelled) setHuOptions([]);
        })
        .finally(() => {
          if (!cancelled) setHuLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [huQuery, open]);

  const title = useMemo(() => (mode === 'edit' ? 'Edit CAP mapping' : 'Add CAP mapping'), [mode]);

  const handleSubmit = () => {
    if (!clientName) {
      setError('Client is required');
      return;
    }
    if (!form.csvFacilityName.trim()) {
      setError('CSV facility name is required');
      return;
    }
    if (!form.facilityLocation?.locationId) {
      setError('Mapped facility location is required');
      return;
    }
    setError('');
    onSubmit({
      clientName,
      csvFacilityName: form.csvFacilityName.trim(),
      csvHuName: form.csvHuName.trim() ? form.csvHuName.trim() : null,
      mappedFacilityHuKey: form.huLocation?.locationId ?? null,
      mappedFacilityLocationKey: form.facilityLocation.locationId,
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" data-testid="cap-mapping-form-dialog">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField
          select
          label="CAP client"
          value={clientName}
          onChange={(event) => setClientName(event.target.value)}
          SelectProps={{ native: true }}
          disabled={saving || mode === 'edit'}
          fullWidth
        >
          {clients.map((client) => (
            <option key={client} value={client}>
              {client}
            </option>
          ))}
        </TextField>
        <TextField
          label="CSV facility name"
          value={form.csvFacilityName}
          onChange={(event) => setForm((prev) => ({ ...prev, csvFacilityName: event.target.value }))}
          disabled={saving}
          fullWidth
          required
          inputProps={{ 'data-testid': 'cap-mapping-csv-facility' }}
        />
        <TextField
          label="CSV housing unit name"
          value={form.csvHuName}
          onChange={(event) => setForm((prev) => ({ ...prev, csvHuName: event.target.value }))}
          disabled={saving}
          fullWidth
          helperText="Leave blank for facility-only mappings"
        />
        <Autocomplete
          options={facilityOptions}
          loading={facilityLoading}
          value={form.facilityLocation}
          onChange={(_event, value) => setForm((prev) => ({ ...prev, facilityLocation: value }))}
          onInputChange={(_event, value) => setFacilityQuery(value)}
          getOptionLabel={formatLocationLabel}
          isOptionEqualToValue={(option, value) => option.locationId === value.locationId}
          renderOption={(props, option) => (
            <li {...props} key={option.locationId}>
              <div>
                <div>{formatLocationLabel(option)}</div>
                {option.parentLocationName ? (
                  <div style={{ color: '#666', fontSize: 12 }}>Parent: {option.parentLocationName}</div>
                ) : null}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Mapped facility location"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {facilityLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={saving}
        />
        <Autocomplete
          options={huOptions}
          loading={huLoading}
          value={form.huLocation}
          onChange={(_event, value) => setForm((prev) => ({ ...prev, huLocation: value }))}
          onInputChange={(_event, value) => setHuQuery(value)}
          getOptionLabel={formatLocationLabel}
          isOptionEqualToValue={(option, value) => option.locationId === value.locationId}
          renderOption={(props, option) => (
            <li {...props} key={option.locationId}>
              {formatLocationLabel(option)}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Mapped housing unit location"
              helperText="Optional; must be under the facility"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {huLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={saving}
        />
        {error ? <div style={{ color: '#c62828' }}>{error}</div> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving} data-testid="cap-mapping-save">
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

MappingFormDialog.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialClientName: PropTypes.string,
  initialMapping: PropTypes.object,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  saving: PropTypes.bool,
};

MappingFormDialog.defaultProps = {
  initialClientName: '',
  initialMapping: null,
  saving: false,
};

export default MappingFormDialog;
